import React, { ReactElement, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import ConnectedFormContext from '../../Context/ConnectedFormContext';
import { isNonDraftOrder, isPendingPriceApproval } from '../../../utils/SalesUtils';
import { IWorkspaceAction } from '../../../@types/workspaceTypes';
import { WorkspaceActionHeader } from '../ActionHeader';
import { DispatchModal, DispatchSource } from './DispatchModal';
import { SalesOrderAction } from '../../../actions/sales/SalesOrderAction';
import { IFormAction } from '../../../@types/entityTypes';
import { IConfirmationTriggeringAction } from '../../connectedForm/Hooks/usePerformSave';

export const useFormDispatchAction = (performSave: IConfirmationTriggeringAction) => {
  const dispatch = useDispatch();

  const { change, values, dirty } = useContext(ConnectedFormContext);

  const onOpenDispatch = (callback: () => void) => {
    if (dirty) {
      dispatch(change(`orderLines.suppressMetaActions`, true));
      performSave(
        {
          onSaveComplete: callback,
          submitMeta: { suppressAfterSubmit: true },
        },
        { ...values, suppressMetaActions: true }
      );
      return;
    }
    callback();
  };

  return useDispatchAction({
    onOpenDispatch: onOpenDispatch,
    condition: (order) => order.id && isNonDraftOrder(order) && !isPendingPriceApproval(order),
    source: DispatchSource.SalesOrderForm,
  });
};

// eslint-disable-next-line complexity
export const useDispatchAction = ({
  onOpenDispatch = (callback) => callback(),
  condition,
  source,
}: {
  onOpenDispatch?: (callback: () => void) => void;
  condition?: (value: any) => boolean;
  source: DispatchSource;
}) => {
  const [dispatchModalState, setDispatchModalState] = useState({ modalOpen: false });

  const result: [IWorkspaceAction | IFormAction<SalesOrderAction>, ReactElement] = [
    {
      key: 'dispatchButton',
      text: 'Dispatch',
      onClick: () => {
        onOpenDispatch(() => {
          setDispatchModalState({ modalOpen: true });
        });
      },
      actionReference: SalesOrderAction.DispatchOrder,
      header: WorkspaceActionHeader.PROCESS_ORDERS,
      condition: condition,
      'data-testid': 'action-dispatch-button',
    },
    <DispatchModal
      isOpen={dispatchModalState.modalOpen}
      onClose={() => {
        setDispatchModalState({ modalOpen: false });
      }}
      source={source}
    />,
  ];

  return result;
};
